import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Settings, Edit3, ArrowLeft, Sparkles } from "lucide-react";
import AddToSquadButton from "./AddToSquadButton";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import SignInPrompt from "@/pages/member/SignInPrompt";
import { ScrimHero, PosterViewer, CompactCard, useWidthBand } from "./feedCard";
import { IkoMix } from "@/design-system/IkoMix";
import { IKO as IKO_TOKENS } from "@/design-system";
import useSafeBack from "@/hooks/useSafeBack";
import { isEventPast } from "@/lib/pastRule";
const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${API}${u}`;
}

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * D46 Addendum 3 §E — Profile pages. Two entry points share this shell:
 * · /app/me  — the signed-in user's own profile (Settings button visible).
 * · /app/people/:handle — public profile view (Settings hidden).
 *
 * Phase-one status:
 * · following / followers · reviews · media — NO backend yet → honest empty states.
 * · going / interested — real, hitting /api/me/attendance-intents when signed in.
 * · attended — real, hitting /api/me/attended when signed in.
 * · organised — real, hitting /api/public/organisers/:handle (own_handle only if set).
 */
export default function ProfilePage({ mode = "me" }) {
  const { handle: routeHandle } = useParams();
  const { user, loading: authLoading } = useAuth();
  // W-C.34 round 2 (Vania 7 Oct) · attended counter · pulls from
  // /me/attended when the viewer is on their own profile. The viewed
  // person's attended list is not public yet, so for others we show 0
  // with the honest "attended" label (same behaviour as before).
  const [attendedCount, setAttendedCount] = useState(0);
  const nav = useNavigate();
  const onBack = useSafeBack("/app");
  const { band } = useWidthBand();
  const [tab, setTab] = useState("going");
  const [rows, setRows] = useState({ going: [], interested: [], media: [], reviews: [], organised: [], iWent: [] });
  const [myReviews, setMyReviews] = useState([]);
  const [myPhotos, setMyPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [prefsCount, setPrefsCount] = useState(null);   // Msg 216 §1b — count of set fields
  const [prefs, setPrefs] = useState({});               // Msg 232 · full prefs for taste card
  // Msg 234 §5 (Vania 30 Sep evening) · real follow counts + list.
  const [followSummary, setFollowSummary] = useState(null);
  const [showFollowList, setShowFollowList] = useState(null); // "followers" | "following" | null
  // Vania 7 Oct 2026 bug · identity (photo, name, bio, cover, membership
  // state) MUST come from the person being viewed. `useAuth().user` is
  // only valid when the viewer is on their OWN profile. For public views
  // we hydrate from /public/users/:handle/profile.
  const [viewed, setViewed] = useState(null);
  // W-C.28 (Vania 7 Oct) · the signed-in user's live taste-mix for the
  // profile IkoMix. Null when loading, {} when empty · the IkoMix
  // component hides itself on empty counts.
  const [ownMix, setOwnMix] = useState(null);

  const isOwn = mode === "me"
    || (!!user?.handle && !!routeHandle && (user.handle || "").toLowerCase() === (routeHandle || "").toLowerCase());
  const handle = isOwn ? (user?.handle || user?.email?.split("@")[0] || "you") : (routeHandle || "");
  // Who the identity fields read from. On own profile that's `user` (the
  // signed-in record · full fields incl. seat number). On public that's
  // the `viewed` doc fetched from the public endpoint, with a safe empty
  // fallback while loading so we never flash the viewer's photo.
  const person = isOwn ? user : viewed;
  const displayName = (person?.name || person?.display_name || handle || "").toString();
  const viewedPhoto = person?.photo_url || person?.picture || "";

  useEffect(() => {
    if (isOwn && !user) return;
    let alive = true;
    setLoading(true);
    const jobs = [];
    if (isOwn) {
      jobs.push(client.get("/me/attendance-intents", { headers: bearer() }).then((r) => {
        if (!alive) return;
        const g = (r.data.intents || []).filter((i) => i.state === "going");
        const int_ = (r.data.intents || []).filter((i) => i.state === "interested");
        setRows((old) => ({ ...old, going: g, interested: int_ }));
      }).catch(() => {}));
      // WW-166 (Vania 13 Oct · D-4 QA) · hydrate i-went for the past
      // events pool used by the Reviews + Media tabs.
      jobs.push(client.get("/me/i-went", { headers: bearer() }).then((r) => {
        if (!alive) return;
        setRows((old) => ({ ...old, iWent: r.data.events || [] }));
      }).catch(() => {}));
    }
    if (handle) {
      jobs.push(client.get(`/public/organisers/${encodeURIComponent(handle)}`).then((r) => {
        if (!alive) return;
        setRows((old) => ({ ...old, organised: r.data.events || [] }));
      }).catch(() => {}));
    }
    Promise.all(jobs).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [isOwn, user, handle]);

  // Vania 7 Oct · fetch the viewed person's public profile when it isn't us.
  useEffect(() => {
    if (isOwn) { setViewed(null); return; }
    const h = (handle || "").trim().toLowerCase();
    if (!h) return;
    let alive = true;
    setViewed(null); // clear stale while we fetch · prevents viewer's photo flash
    client.get(`/public/users/${encodeURIComponent(h)}/profile`)
      .then((r) => { if (alive) setViewed(r.data || null); })
      .catch(() => { if (alive) setViewed(null); });
    return () => { alive = false; };
  }, [isOwn, handle]);

  // Msg 216 §1b — probe preferences so the "your taste" row shows the state.
  useEffect(() => {
    if (!isOwn || !user) return;
    client.get("/me/preferences", { headers: bearer() }).then((r) => {
      const p = r.data.preferences || {};
      setPrefs(p);
      const n = ["feelings", "liked_ikos", "company", "areas", "budget", "nights", "moments"]
        .reduce((a, k) => a + (Array.isArray(p[k]) ? (p[k].length ? 1 : 0) : (p[k] ? 1 : 0)), 0);
      setPrefsCount(n);
    }).catch(() => setPrefsCount(0));
  }, [isOwn, user]);

  // W-C.28 (Vania 7 Oct) · live taste mix for own profile.
  useEffect(() => {
    if (!isOwn || !user) { setOwnMix(null); return; }
    client.get("/me/taste-mix", { headers: bearer() })
      .then((r) => setOwnMix(r.data || null))
      .catch(() => setOwnMix(null));
  }, [isOwn, user]);

  // WW-166 (Vania 13 Oct · D-4 QA) · hydrate the viewed person's REAL
  // reviews + photos for the Reviews + Media tabs. Works for own
  // profile (uses user.user_id) and someone else's (uses viewed.user_id
  // from the public profile fetch).  No prompts on someone else's view.
  const viewedUserId = isOwn ? user?.user_id : viewed?.user_id;
  useEffect(() => {
    if (!viewedUserId) { setMyReviews([]); setMyPhotos([]); return; }
    let alive = true;
    Promise.all([
      client.get(`/users/${encodeURIComponent(viewedUserId)}/reviews`).catch(() => ({ data: { reviews: [] } })),
      client.get(`/users/${encodeURIComponent(viewedUserId)}/posts`).catch(() => ({ data: { posts: [] } })),
    ]).then(([rev, pst]) => {
      if (!alive) return;
      setMyReviews(rev.data.reviews || []);
      setMyPhotos((pst.data.posts || []).filter((p) => Array.isArray(p.media_urls) && p.media_urls.length > 0));
    });
    return () => { alive = false; };
  }, [viewedUserId]);

  // W-C.34 round 2 · attended count from /me/attended.
  useEffect(() => {
    if (!isOwn || !user) { setAttendedCount(0); return; }
    client.get("/me/attended", { headers: bearer() })
      .then((r) => setAttendedCount((r.data?.attended || []).length))
      .catch(() => setAttendedCount(0));
  }, [isOwn, user]);

  // Msg 234 §5 · fetch real follow summary. Uses handle so it works for both
  // own and public profiles without knowing the user_id up front.
  useEffect(() => {
    const h = (handle || "").toLowerCase();
    if (!h) return;
    let alive = true;
    client.get(`/public/users/${encodeURIComponent(h)}/follow-summary`)
      .then((r) => { if (alive) setFollowSummary(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [handle]);

  const counters = useMemo(() => ({
    attended: attendedCount, // W-C.34 round 2 · pulled from /me/attended
    organised: rows.organised.length,
    reviews: 0,
  }), [rows.organised]);

  // Vania Msg 220 §2a — guests get the in-app SignInPrompt instead of a
  // bounce to /admin/login. Placed AFTER every hook so React's rules-of-hooks
  // stays intact across renders.
  if (isOwn && !authLoading && !user) {
    return <SignInPrompt title="your profile"
                         line="sign in to see the events you're going to, the ones you've been to, and to build your taste."
                         next="/app/me" testId="profile-signin" />;
  }

  return (
    <div className="flex h-full flex-col" data-testid={`profile-page-${mode}`}
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={`${(displayName || "profile").toLowerCase()} · watu wako`} />
      <div className="flex-1 overflow-y-auto pb-10" style={{ overscrollBehavior: "contain" }}>
        {/* Wave 14.1a (Vania 2 Oct) · bump the cover + first content block down
            so the overlapping avatar circle clears the sticky member topbar on
            desktop at every viewport. Previously `height=72` + `marginTop:-30`
            put the avatar top at viewport Y ≈ 42px, which clipped under the
            56px sticky header. New stack: 20px gutter · 100px cover · avatar
            overlaps by 36px, so the circle top sits ≥ 84px below the topbar. */}
        <div style={{ height: 20 }} aria-hidden />
        <ScrimHero image={person?.cover_image_url} height={100} testId="profile-cover">
          {!isOwn && (
            <button aria-label="back" data-testid="profile-back" onClick={onBack}
                    style={{ position: "absolute", left: 12, top: 12, width: 36, height: 36,
                             borderRadius: 999, background: "var(--ww-surface)", border: `1px solid var(--ww-line)`,
                             color: "var(--ww-text)", display: "grid", placeItems: "center", cursor: "pointer" }}>
              <ArrowLeft size={16} />
            </button>
          )}
        </ScrimHero>

        {/* Wave 15 (Vania 3 Oct) · avatar MUST sit above the cream header band
            with no clip. Use a positioned wrapper so overflow on ScrimHero
            never slices the circle. 3px ring in the page ground reads clean
            over the band for both photo and letter avatars. Edit button now
            lives inline on the name row so it never leaves the content
            column at any breakpoint. */}
        <div className="ww-container" style={{ marginTop: -36, paddingTop: 8, position: "relative", zIndex: 5 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, position: "relative", zIndex: 5 }}>
            {viewedPhoto ? (
              <img data-testid="profile-avatar" src={absUrl(viewedPhoto)} alt=""
                   style={{ width: 76, height: 76, borderRadius: 999, objectFit: "cover",
                            boxShadow: "0 0 0 3px var(--ww-ground)",
                            position: "relative", zIndex: 5 }} />
            ) : (
              <div data-testid="profile-avatar"
                   style={{ width: 76, height: 76, borderRadius: 999,
                            background: "#FFD60A", color: "#1A1A30",
                            boxShadow: "0 0 0 3px var(--ww-ground)",
                            display: "grid", placeItems: "center",
                            fontFamily: "Poppins", fontWeight: 800, fontSize: 26,
                            textTransform: "uppercase",
                            position: "relative", zIndex: 5 }}>
                {(displayName || handle || "?").slice(0, 1)}
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "flex-start",
                         justifyContent: "space-between", gap: 12, marginTop: 12 }}>
            <h1 className="lowercase"
                data-testid="profile-name"
                style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.03em",
                         fontSize: 32, lineHeight: 1.02, flex: 1, minWidth: 0 }}>
              {(displayName || "").toLowerCase()}
            </h1>
            {isOwn && (
              <button aria-label="edit profile" data-testid="profile-settings"
                      onClick={() => nav("/app/me/edit")}
                      style={{ width: 40, height: 40, flexShrink: 0,
                               borderRadius: 999, background: "var(--ww-surface)",
                               border: `1px solid var(--ww-line)`,
                               color: "var(--ww-text)", display: "grid",
                               placeItems: "center", cursor: "pointer" }}>
                <Edit3 size={16} />
              </button>
            )}
          </div>
          {handle && (
            <p style={{ fontFamily: "Arial", fontSize: 14, color: META }}>@{handle.toLowerCase()}</p>
          )}
          {person?.bio && (
            <p data-testid="profile-bio"
               style={{ fontFamily: "Arial", fontSize: 14, color: "var(--ww-text)", marginTop: 8, maxWidth: 640, lineHeight: 1.4 }}>
              {person.bio}
            </p>
          )}

          {/* W-C.28 (Vania 7 Oct 2026) · profile iko-mix driven by the real
              /me/taste-mix (own) or /public/users/:handle/profile (viewed).
              Hidden entirely if the person has no data yet · the IkoMix
              component itself bails on empty counts, so this slot stays
              invisible instead of showing a bar + no chips. */}
          {person && !person.hide_iko_mix && (
            <div data-testid="profile-iko-mix" style={{ marginTop: 10, maxWidth: 420 }}>
              <IkoMix counts={ownMix?.counts || person.iko_mix?.counts || {}}
                       total={ownMix?.total || person.iko_mix?.total || null}
                       compact label="mostly" />
            </div>
          )}

          <p data-testid="profile-membership-pill" style={{ marginTop: 12 }}>
            <MembershipPill user={isOwn ? user : { ...(viewed || {}), is_me: false }} />
          </p>
          {/* Msg 251 §6 · add-to-squad on any other member's profile. */}
          {!isOwn && user && handle && (
            <div style={{ marginTop: 12, display: "inline-flex" }}>
              <AddToSquadButton targetHandle={handle} testIdPrefix="profile-" />
            </div>
          )}

          {/* Msg 234 §5 · real followers/following. Clickable to open the list. */}
          <div className="mt-2 flex gap-4" data-testid="profile-follow-strip">
            <button type="button" data-testid="profile-following-count"
                    onClick={() => setShowFollowList("following")}
                    disabled={!(followSummary?.following_count)}
                    style={{ background: "transparent", border: 0, padding: 0, cursor: (followSummary?.following_count) ? "pointer" : "default" }}>
              <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                <strong style={{ color: "var(--ww-text)" }}>{followSummary?.following_count ?? 0}</strong> following
              </span>
            </button>
            <button type="button" data-testid="profile-followers-count"
                    onClick={() => setShowFollowList("followers")}
                    disabled={!(followSummary?.followers_count)}
                    style={{ background: "transparent", border: 0, padding: 0, cursor: (followSummary?.followers_count) ? "pointer" : "default" }}>
              <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                <strong style={{ color: "var(--ww-text)" }}>{followSummary?.followers_count ?? 0}</strong> followers
              </span>
            </button>
          </div>

          {/* Msg 247 · counters strip · attended · organised · friends. */}
          <div className="mt-4 grid gap-2" data-testid="profile-counters"
               style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <Counter label="attended" value={counters.attended} testId="counter-attended" />
            <Counter label="curated" value={counters.organised} testId="counter-organised" />
            <Counter label="friends" value={followSummary?.friends_count ?? 0} testId="counter-friends" />
          </div>

          {/* Msg 232 · "your taste" quiet card above tabs. iko-mix bar based
              on quiz picks (adaptive weighting shipped in a follow-up round).
              Retake asks for confirm first. */}
          {isOwn && (
            <TasteCard prefs={prefs} onRetake={async () => {
              if (window.confirm("this resets your picks, ready?")) nav("/app/welcome?edit=1");
            }} />
          )}

          {/* Tabs */}
          <div className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="profile-tabs"
               style={{ scrollbarWidth: "none" }}>
            {["going", "interested", "media", "reviews", "curated"].map((t) => (
              <button key={t} onClick={() => setTab(t === "curated" ? "organised" : t)} data-testid={`profile-tab-${t}`}
                      style={{ padding: "10px 16px", borderRadius: 999,
                               background: (tab === t || (t === "curated" && tab === "organised")) ? "var(--ww-text)" : "transparent",
                               color: (tab === t || (t === "curated" && tab === "organised")) ? "var(--ww-ground)" : "var(--ww-text)",
                               border: `1px solid ${(tab === t || (t === "curated" && tab === "organised")) ? "var(--ww-text)" : "var(--ww-line-strong)"}`,
                               fontFamily: "Poppins", fontWeight: 600, fontSize: 13, textTransform: "lowercase",
                               minHeight: 44, cursor: "pointer", whiteSpace: "nowrap" }}>
                {t}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <TabContent tab={tab} rows={rows} setRows={setRows} isOwn={isOwn}
                        setViewer={setViewer} loading={loading}
                        myReviews={myReviews} myPhotos={myPhotos} />
          </div>
          {isOwn && <ProfileLegalLinks />}
        </div>
      </div>
      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
      {showFollowList && (
        <FollowListModal
          title={showFollowList === "followers" ? "followers" : "following"}
          rows={(showFollowList === "followers" ? followSummary?.followers : followSummary?.following) || []}
          onClose={() => setShowFollowList(null)} />
      )}
    </div>
  );
}

function FollowListModal({ title, rows, onClose }) {
  return (
    <div data-testid="follow-list-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(18,18,42,0.85)",
                  display: "grid", placeItems: "center", zIndex: 60, padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 460, background: CARD,
                    border: `1px solid ${LINE}`, borderRadius: 16, padding: 16,
                    maxHeight: "76dvh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-3">
          <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18, textTransform: "lowercase", color: "var(--ww-text)" }}>
            {title}
          </p>
          <button data-testid="follow-list-close" onClick={onClose}
                  aria-label="close"
                  style={{ width: 40, height: 40, borderRadius: 999, background: "transparent",
                           border: `1px solid ${LINE}`, color: "var(--ww-text)", cursor: "pointer",
                           fontFamily: "Poppins", fontWeight: 600, fontSize: 18 }}>×</button>
        </div>
        {rows.length === 0 && (
          <p style={{ fontFamily: "Arial", fontSize: 13, color: META, padding: "8px 4px" }}>
            no one here yet.
          </p>
        )}
        {rows.map((p) => {
          const to = p.kind === "organiser"
            ? `/app/organiser/${p.handle}` : `/app/people/${p.handle}`;
          return (
            <Link key={`${p.kind}-${p.handle}`} to={to}
                  onClick={onClose}
                  data-testid={`follow-list-row-${p.handle}`}
                  style={{ display: "flex", alignItems: "center", gap: 10,
                           padding: "10px 4px", borderTop: `1px solid ${LINE}`,
                           textDecoration: "none", color: "inherit" }}>
              {p.photo_url ? (
                <img src={p.photo_url} alt=""
                     style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover" }} />
              ) : (
                <span style={{ width: 40, height: 40, borderRadius: 999, background: "var(--ww-sunken)",
                                color: "var(--ww-text)", border: `1px solid ${LINE}`,
                                display: "grid", placeItems: "center",
                                fontFamily: "Poppins", fontWeight: 800, fontSize: 14 }}>
                  {(p.name || p.handle || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="lowercase"
                   style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14, color: "var(--ww-text)",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {(p.name || p.handle || "").toLowerCase()}
                </p>
                <p style={{ fontFamily: "Arial", fontSize: 12, color: META,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  @{(p.handle || "").toLowerCase()} · {p.kind}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Counter({ label, value, testId }) {
  return (
    <div data-testid={testId} style={{
      background: CARD, border: `1px solid ${LINE}`, borderRadius: 12,
      padding: "10px 12px", textAlign: "center",
    }}>
      <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 20 }}>{value}</p>
      <p style={{ fontFamily: "Arial", fontSize: 11, color: META, textTransform: "uppercase", letterSpacing: "0.18em" }}>
        {label}
      </p>
    </div>
  );
}

function TabContent({ tab, rows, setRows, isOwn, setViewer, loading, myReviews, myPhotos }) {
  if (loading) return <p style={{ fontFamily: "Arial", color: META }}>loading…</p>;

  // WW-166 (Vania 13 Oct · D-4 QA) · going + interested lists only
  // events that are upcoming or on-now. The moment an event is past
  // (shared past rule from WW-159 + WW-161 night rule), it leaves
  // both tabs.
  const upcoming = (items) => (items || []).filter((it) => !isEventPast(it.event || it));
  const past = (items) => (items || []).filter((it) => isEventPast(it.event || it));
  const goingUpcoming = upcoming(rows.going);
  const interestedUpcoming = upcoming(rows.interested);
  const interestedPast = past(rows.interested);

  if ((tab === "going" || tab === "interested") && !isOwn) {
    return <Empty msg="only the person can see their going and interested lists." />;
  }

  if (tab === "media") {
    return <MediaTab rows={rows} isOwn={isOwn} myPhotos={myPhotos}
                     setViewer={setViewer} />;
  }
  if (tab === "reviews") {
    return <ReviewsTab rows={rows} isOwn={isOwn} myReviews={myReviews}
                       setViewer={setViewer} />;
  }

  // WW-166 change (Vania 13 Oct · D-4 QA) · the interested tab keeps
  // its upcoming cards at the top AND surfaces past-interested rows
  // underneath in a "did you go?" section with "i went" / "no" per
  // card.  Someone else's profile never shows the section.
  if (tab === "interested") {
    return (
      <InterestedTab upcoming={interestedUpcoming} pastRows={interestedPast}
                     isOwn={isOwn} setRows={setRows} setViewer={setViewer} />
    );
  }

  const cards = tab === "going" ? goingUpcoming
              : tab === "organised" ? (rows.organised || [])
              : [];

  if (!cards || cards.length === 0) {
    const msg = tab === "going" ? "nothing marked going yet. tap i'm going on an event."
              : "no upcoming events curated.";
    return <Empty msg={msg} />;
  }
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
      {cards.map((e) => <CompactCard key={e.id || e.event_id} event={e.event || e} onOpenPoster={setViewer} />)}
    </div>
  );
}

function InterestedTab({ upcoming, pastRows, isOwn, setRows, setViewer }) {
  const nav = useNavigate();
  const [busyId, setBusyId] = React.useState("");

  const removeFromInterested = async (eventId, { markWent }) => {
    setBusyId(eventId);
    try {
      if (markWent) {
        await client.post(`/events/${eventId}/i-went`, {}, { headers: bearer() });
      }
      // Clear the "interested" intent either way (so the card leaves
      // the did-you-go section).  "no" is a quiet dismiss, no server
      // mark; "i went" moves it into reviews + media via /i-went.
      try {
        await client.put(`/events/${eventId}/attendance/intent`,
          { state: "none" }, { headers: bearer() });
      } catch {}
      setRows((old) => {
        const next = { ...old };
        next.interested = (old.interested || []).filter(
          (r) => (r.event?.id || r.id) !== eventId,
        );
        if (markWent) {
          const ev = (pastRows.find((r) => (r.event?.id || r.id) === eventId) || {}).event
                     || pastRows.find((r) => (r.event?.id || r.id) === eventId);
          if (ev) {
            const seen = new Set((old.iWent || []).map((e) => e.id));
            if (!seen.has(ev.id)) next.iWent = [...(old.iWent || []), ev];
          }
        }
        return next;
      });
    } finally {
      setBusyId("");
    }
  };

  const hasUpcoming = upcoming && upcoming.length > 0;
  const hasPast = isOwn && pastRows && pastRows.length > 0;
  if (!hasUpcoming && !hasPast) {
    return <Empty msg="nothing marked interested yet." />;
  }

  return (
    <div data-testid="interested-tab" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {hasUpcoming && (
        <div className="grid gap-3"
             style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {upcoming.map((it) => (
            <CompactCard key={(it.event?.id || it.id)}
                         event={it.event || it}
                         onOpenPoster={setViewer} />
          ))}
        </div>
      )}
      {hasPast && (
        <section data-testid="interested-did-you-go">
          <h3 className="lowercase"
              style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18,
                       color: "var(--ww-text)", marginBottom: 10 }}>
            did you go?
          </h3>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
            {pastRows.map((it) => {
              const ev = it.event || it;
              const eid = ev.id;
              return (
                <div key={eid} data-testid={`interested-past-card-${eid}`}
                     className="flex flex-col gap-2">
                  <CompactCard event={ev} onOpenPoster={setViewer} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    <button data-testid={`interested-i-went-${eid}`}
                            disabled={busyId === eid}
                            onClick={() => removeFromInterested(eid, { markWent: true })}
                            style={{ padding: "10px 14px", borderRadius: 10,
                                     background: "#FFD60A", color: "#1A1A30",
                                     fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                                     textTransform: "lowercase", minHeight: 40,
                                     border: "1px solid #FFD60A",
                                     cursor: busyId === eid ? "wait" : "pointer",
                                     opacity: busyId === eid ? 0.6 : 1 }}>
                      i went
                    </button>
                    <button data-testid={`interested-no-${eid}`}
                            disabled={busyId === eid}
                            onClick={() => removeFromInterested(eid, { markWent: false })}
                            style={{ padding: "10px 14px", borderRadius: 10,
                                     background: "transparent", color: "var(--ww-text)",
                                     border: "1px solid var(--ww-line-strong)",
                                     fontFamily: "Poppins", fontWeight: 600, fontSize: 13,
                                     textTransform: "lowercase", minHeight: 40,
                                     cursor: busyId === eid ? "wait" : "pointer",
                                     opacity: busyId === eid ? 0.6 : 1 }}>
                      no
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * W-C.10 (Vania 7 Oct 2026) · Media tab empty state in Discover style.
 *
 * The old empty state said "media lands with the members build". The new
 * surface shows the person's PAST events as real Discover-style cards (the
 * same CompactCard used everywhere), each with three quick actions:
 *   · i went  → POST /me/attendance-intents (state=went) and refresh
 *   · add photos / write a review → navigates to the event page with a
 *     query param so EventComposers opens on arrival
 *
 * The small "no photos yet. be the first." line stays above the list. On
 * someone else's profile we use their `organised` events as the past list
 * (their going/interested are private).
 */
function MediaTab({ rows, isOwn, myPhotos, setViewer }) {
  // WW-166 (Vania 13 Oct · D-4 QA) · the past-events pool here is
  // strictly events the person was GOING to or marked I WENT. An
  // event they were only INTERESTED in and did not go to drops out.
  // Someone else's profile shows ONLY the real photos they posted
  // (no cards, no prompts).
  const nav = useNavigate();
  const seenPhotoEvents = React.useMemo(() => {
    const s = new Set();
    (myPhotos || []).forEach((p) => { if (p?.event_id) s.add(p.event_id); });
    return s;
  }, [myPhotos]);

  const pastWentOrGoing = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (ev) => {
      if (!ev || !ev.id || seen.has(ev.id)) return;
      if (!isEventPast(ev)) return;
      seen.add(ev.id);
      out.push(ev);
    };
    (rows.going || []).forEach((r) => push(r.event || r));
    (rows.iWent || []).forEach((ev) => push(ev));
    out.sort((a, b) => (b.start_at || "").localeCompare(a.start_at || ""));
    return out;
  }, [rows.going, rows.iWent]);

  // Someone else's profile: real photos only, nothing else.
  if (!isOwn) {
    if (!myPhotos || myPhotos.length === 0) {
      return <Empty msg="no photos yet. after an event you went to, add a photo here." />;
    }
    return <PhotoGrid photos={myPhotos} setViewer={setViewer} />;
  }

  // Own profile: real photos first, then prompt cards for past events
  // without a photo yet.
  const promptPool = pastWentOrGoing.filter((ev) => !seenPhotoEvents.has(ev.id));
  if ((!myPhotos || myPhotos.length === 0) && promptPool.length === 0) {
    return <Empty msg="no photos yet. after an event you went to, add a photo here." />;
  }
  return (
    <div data-testid="media-tab" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {myPhotos && myPhotos.length > 0 && <PhotoGrid photos={myPhotos} setViewer={setViewer} />}
      {promptPool.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {promptPool.map((ev) => (
            <div key={ev.id} data-testid={`media-prompt-card-${ev.id}`}
                 className="flex flex-col gap-2">
              <CompactCard event={ev} onOpenPoster={setViewer} />
              <button data-testid={`media-add-photo-${ev.id}`}
                      onClick={() => nav(`/app/event/${ev.id}?compose=photo`)}
                      style={{ padding: "10px 14px", borderRadius: 10,
                               background: "#FFD60A", color: "#1A1A30",
                               fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                               textTransform: "lowercase", minHeight: 40,
                               border: "1px solid #FFD60A", cursor: "pointer" }}>
                add a photo
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoGrid({ photos, setViewer }) {
  const flat = [];
  for (const p of photos || []) {
    for (const url of p.media_urls || []) flat.push({ id: `${p.id}-${url}`, url, event_id: p.event_id });
  }
  if (flat.length === 0) return null;
  return (
    <div data-testid="profile-photo-grid"
         className="grid gap-2"
         style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
      {flat.map((p) => (
        <button key={p.id} data-testid={`profile-photo-${p.id}`}
                onClick={() => setViewer && setViewer({ src: p.url, alt: "" })}
                style={{ padding: 0, border: 0, background: "transparent", cursor: "zoom-in",
                         aspectRatio: "1 / 1", overflow: "hidden", borderRadius: 10 }}>
          <img src={p.url} alt="" loading="lazy"
               style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </button>
      ))}
    </div>
  );
}

function ReviewsTab({ rows, isOwn, myReviews, setViewer }) {
  // WW-166 · same past-event pool rule as MediaTab (going OR i-went,
  // not interested-only).  Someone else's profile shows only real
  // reviews.
  const nav = useNavigate();
  const seenReviewEvents = React.useMemo(() => {
    const s = new Set();
    (myReviews || []).forEach((r) => { if (r?.event_id) s.add(r.event_id); });
    return s;
  }, [myReviews]);

  const pastWentOrGoing = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (ev) => {
      if (!ev || !ev.id || seen.has(ev.id)) return;
      if (!isEventPast(ev)) return;
      seen.add(ev.id);
      out.push(ev);
    };
    (rows.going || []).forEach((r) => push(r.event || r));
    (rows.iWent || []).forEach((ev) => push(ev));
    out.sort((a, b) => (b.start_at || "").localeCompare(a.start_at || ""));
    return out;
  }, [rows.going, rows.iWent]);

  if (!isOwn) {
    if (!myReviews || myReviews.length === 0) {
      return <Empty msg="no reviews yet. after an event you went to, review it here." />;
    }
    return <ReviewList reviews={myReviews} />;
  }

  const promptPool = pastWentOrGoing.filter((ev) => !seenReviewEvents.has(ev.id));
  if ((!myReviews || myReviews.length === 0) && promptPool.length === 0) {
    return <Empty msg="no reviews yet. after an event you went to, review it here." />;
  }
  return (
    <div data-testid="reviews-tab" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {myReviews && myReviews.length > 0 && <ReviewList reviews={myReviews} />}
      {promptPool.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {promptPool.map((ev) => (
            <div key={ev.id} data-testid={`reviews-prompt-card-${ev.id}`}
                 className="flex flex-col gap-2">
              <CompactCard event={ev} onOpenPoster={setViewer} />
              <button data-testid={`reviews-review-event-${ev.id}`}
                      onClick={() => nav(`/app/event/${ev.id}?compose=review`)}
                      style={{ padding: "10px 14px", borderRadius: 10,
                               background: "#FFD60A", color: "#1A1A30",
                               fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                               textTransform: "lowercase", minHeight: 40,
                               border: "1px solid #FFD60A", cursor: "pointer" }}>
                review this event
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewList({ reviews }) {
  return (
    <div data-testid="profile-review-list"
         style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {reviews.map((r) => (
        <article key={r.id} data-testid={`profile-review-${r.id}`}
                 className="rounded-xl border p-3"
                 style={{ borderColor: LINE, background: CARD }}>
          <p style={{ fontFamily: "Arial", fontSize: 11, color: META,
                      textTransform: "uppercase", letterSpacing: "0.18em" }}>
            {(r.event_title || "event").toLowerCase()}
          </p>
          {r.rating != null && (
            <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 16, color: "var(--ww-text)" }}>
              {r.rating}
            </p>
          )}
          {r.body && (
            <p style={{ fontFamily: "Arial", fontSize: 14, color: "var(--ww-text)",
                        marginTop: 4, whiteSpace: "pre-wrap" }}>
              {r.body}
            </p>
          )}
        </article>
      ))}
    </div>
  );
}

function Empty({ msg }) {  return (
    <div className="rounded-2xl border p-8 text-center" data-testid="profile-empty"
         style={{ borderColor: LINE, background: CARD }}>
      <p style={{ fontFamily: "Arial", fontSize: 14, color: META }}>{msg}</p>
      <Link to="/app" style={{ display: "inline-block", marginTop: 14, padding: "10px 20px", borderRadius: 999,
                                background: "#FFD60A", color: "#1A1A30",
                                fontFamily: "Poppins", fontWeight: 600, textDecoration: "none", textTransform: "lowercase" }}>
        open for you
      </Link>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Msg 232 · Membership pill + Taste card
// ---------------------------------------------------------------------------
const MOMENT_LABEL = {
  saturday_night_dance_floor: "saturday night on the dance floor",
  late_night_gig: "a late night gig",
  amapiano_day_party: "an amapiano day party",
  karaoke_night_crew: "karaoke night with the crew",
  friday_rooftop_party: "a friday rooftop party",
  festival_weekend: "a festival weekend",
  tuesday_jazz_night: "a tuesday jazz night",
  friday_drinks_after_work: "friday drinks after work",
  saturday_afternoon_gallery: "a saturday afternoon gallery",
  sunday_brunch_runs_long: "a sunday brunch that runs long",
  rooftop_at_sunset: "a rooftop at sunset",
  market_day: "a market day",
  saturday_morning_hike: "a saturday morning hike",
  sunday_afternoon_match: "a sunday afternoon match",
  games_night_with_friends: "games night with friends",
  morning_run_club: "a morning run club",
  padel_after_work: "padel after work",
  big_game_sports_bar: "the big game at a sports bar",
  weeknight_talk_or_workshop: "a weeknight talk or workshop",
  founders_breakfast: "a founders breakfast",
  lunchtime_networking: "a lunchtime networking meetup",
  weekend_masterclass: "a weekend masterclass",
  pitch_night: "a pitch night",
  creative_coworking_day: "a creative coworking day",
};
const MOMENT_IKO = {
  saturday_night_dance_floor: "sherehe", late_night_gig: "sherehe", amapiano_day_party: "sherehe",
  karaoke_night_crew: "sherehe", friday_rooftop_party: "sherehe", festival_weekend: "sherehe",
  tuesday_jazz_night: "polite", friday_drinks_after_work: "polite", saturday_afternoon_gallery: "polite",
  sunday_brunch_runs_long: "polite", rooftop_at_sunset: "polite", market_day: "polite",
  saturday_morning_hike: "michezo", sunday_afternoon_match: "michezo", games_night_with_friends: "michezo",
  morning_run_club: "michezo", padel_after_work: "michezo", big_game_sports_bar: "michezo",
  weeknight_talk_or_workshop: "kazi", founders_breakfast: "kazi", lunchtime_networking: "kazi",
  weekend_masterclass: "kazi", pitch_night: "kazi", creative_coworking_day: "kazi",
};

export function MembershipPill({ user }) {
  // Wave C · level names only (Vania 7 Oct 2026 final spec).
  //   guest           → no pill (guests see "get in" in the topbar)
  //   in (signed-up)  → "in · become an insider" link to /app/membership
  //   insider (trial) → "insider, N days on trial"
  //   insider (paid)  → "insider"
  //   founding crew   → "founding crew #N"
  //   comped          → "founding crew · complimentary"
  if (!user) return null;
  const s = (user.membership_state || "free").toLowerCase();
  if (s === "trialing") {
    const ends = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
    const daysLeft = ends ? Math.max(0, Math.ceil((ends.getTime() - Date.now()) / 864e5)) : null;
    return (
      <span data-testid="pill-insider"
            style={pillStyle("#FFD60A", "#1A1A30")}>
        insider{daysLeft != null ? `, ${daysLeft} day${daysLeft === 1 ? "" : "s"} on trial` : ""}
      </span>
    );
  }
  if (s === "founding" || user.is_founding) {
    const n = user.founding_seat_number;
    // Vania 7 Oct · seat number shows on OWN profile only (identified by
    // `user.is_me === true` set by ProfilePage when viewing /app/me). Elsewhere
    // (friends list, squad rows, reviews) the pill reads just "founding crew".
    const showSeat = !!user.is_me && n;
    return (
      <span data-testid="pill-founding-crew" style={pillStyle("#FFD60A", "#1A1A30")}>
        founding crew{showSeat ? ` #${n}` : ""}
      </span>
    );
  }
  if (s === "paid") {
    return <span data-testid="pill-insider" style={pillStyle("#FFD60A", "#1A1A30")}>insider</span>;
  }
  if (s === "comped") {
    return <span data-testid="pill-comped" style={pillStyle("#FFD60A", "#1A1A30")}>founding crew · complimentary</span>;
  }
  return (
    <Link to="/app/membership" data-testid="pill-in"
          style={pillStyle("#FFD60A", "#1A1A30")}>
      in · become an insider
    </Link>
  );
}
function pillStyle(bg, fg) {
  return {
    display: "inline-block", padding: "6px 12px", borderRadius: 999,
    background: bg, color: fg,
    fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
    textTransform: "lowercase",
  };
}

export function TasteCard({ prefs, onRetake }) {
  const moments = Array.isArray(prefs?.moments) ? prefs.moments : [];
  const feelings = Array.isArray(prefs?.feelings) ? prefs.feelings : [];
  const areasHome = Array.isArray(prefs?.areas_home) && prefs.areas_home.length
    ? prefs.areas_home : (Array.isArray(prefs?.areas) ? prefs.areas : []);
  // Msg 234 §c · adaptive taste mix — fetch weighted counts from behaviour
  // (went > going > saved/liked) once on mount. Fall back to the quiz-only
  // moment mapping when the endpoint is empty.
  const [adaptive, setAdaptive] = useState(null);
  useEffect(() => {
    const t = localStorage.getItem("session_token");
    if (!t) return;
    fetch(`${API}/api/me/taste-mix`, {
      headers: { Authorization: `Bearer ${t}` }, credentials: "include",
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setAdaptive(d))
      .catch(() => {});
  }, []);
  const quizCounts = { sherehe: 0, polite: 0, michezo: 0, kazi: 0 };
  moments.forEach((k) => { const i = MOMENT_IKO[k]; if (i) quizCounts[i] += 1; });
  const counts = adaptive?.total > 0 ? adaptive.counts : quizCounts;
  const explanation = adaptive?.explanation || "";
  const noAnswers = !moments.length && !feelings.length && !areasHome.length
                    && (!adaptive || adaptive.total === 0);

  return (
    <div data-testid="profile-taste-card"
         style={{ marginTop: 16, background: CARD, border: `1px solid ${LINE}`,
                  borderRadius: 14, padding: 14 }}>
      <div className="flex items-center justify-between">
        <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 15, textTransform: "lowercase", color: "var(--ww-text)" }}>
          your taste
        </p>
        <button data-testid="taste-retake"
                onClick={onRetake}
                style={{ padding: "8px 14px", borderRadius: 999, background: "transparent",
                         border: `1px solid #FFD60A`, color: "var(--ww-text)",
                         fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                         textTransform: "lowercase", cursor: "pointer", minHeight: 40 }}>
          retake the quiz
        </button>
      </div>
      {noAnswers ? (
        <p style={{ fontFamily: "Arial", fontSize: 13, color: META, marginTop: 8 }}>
          take the 7-step quiz so we can tune your for you rail.
        </p>
      ) : (
        <>
          <IkoMixBar counts={counts} />
          {explanation && (
            <p data-testid="taste-explanation"
               style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 8, lineHeight: 1.5 }}>
              {explanation}
            </p>
          )}
          {moments.length > 0 && (
            <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text)", marginTop: 8, lineHeight: 1.4 }}>
              top moments:{" "}
              <span style={{ color: META }}>
                {moments.slice(0, 3).map((k) => MOMENT_LABEL[k] || k).join(" · ")}
              </span>
            </p>
          )}
          {areasHome.length > 0 && (
            <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text)", marginTop: 4 }}>
              usually heads to: <span style={{ color: META }}>
                {areasHome.slice(0, 3).map((a) => String(a).toLowerCase()).join(", ")}
              </span>
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* Wave 12.18 (Vania 1 Oct) · legal links in profile settings. */
function ProfileLegalLinks() {
  return (
    <p data-testid="profile-legal-links"
       style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)",
                margin: "28px 0 12px", textAlign: "center" }}>
      <a href="/terms" data-testid="profile-terms-link"
         style={{ color: "var(--ww-text)", textDecoration: "underline", marginRight: 14 }}>terms</a>
      <a href="/privacy" data-testid="profile-privacy-link"
         style={{ color: "var(--ww-text)", textDecoration: "underline" }}>privacy policy</a>
    </p>
  );
}

function IkoMixBar({ counts }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return null;
  // Msg 234 §4 (Vania 30 Sep evening) · four iko colours only, no yellow.
  const IKO_C = {
    sherehe: IKO_TOKENS.sherehe.bright,
    polite:  IKO_TOKENS.polite.bright,
    michezo: IKO_TOKENS.michezo.bright,
    kazi:    IKO_TOKENS.kazi.bright,
  };
  return (
    <div className="flex" data-testid="taste-mix-bar"
         style={{ height: 6, marginTop: 10, borderRadius: 999, overflow: "hidden", background: "var(--ww-line)" }}>
      {["sherehe","polite","michezo","kazi"].map((k) => {
        const pct = counts[k] ? Math.round((counts[k] / total) * 100) : 0;
        if (!pct) return null;
        return <span key={k} style={{ width: `${pct}%`, background: IKO_C[k], height: "100%" }} />;
      })}
    </div>
  );
}
